import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';

const ManageCategories = ({ token }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_categories.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setCategories(data.categories);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_categories.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'create', category_name: newCategory })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: 'Category added successfully!', type: 'success' });
                setNewCategory('');
                fetchCategories();
            } else {
                setMessage({ text: data.message, type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Error adding category.', type: 'error' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleDelete = async (categoryId) => {
        if (!await customConfirm('Are you sure you want to delete this category?', 'Delete Category', 'danger')) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_categories.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'delete', category_id: categoryId })
            });
            const data = await res.json();
            if (data.success) {
                fetchCategories();
            } else {
                await customAlert(data.message, 'Error', 'danger');
            }
        } catch (err) {
            await customAlert('Error deleting category.', 'Error', 'danger');
        }
    };

    const moveCategory = (index, direction) => {
        if (direction === -1 && index === 0) return;
        if (direction === 1 && index === categories.length - 1) return;
        
        const newCats = [...categories];
        const temp = newCats[index];
        newCats[index] = newCats[index + direction];
        newCats[index + direction] = temp;
        setCategories(newCats);
    };

    const handleSaveOrder = async () => {
        setSaving(true);
        const order = categories.map(c => c.category_id);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_categories.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'reorder', order: order })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: 'Display order saved successfully!', type: 'success' });
            } else {
                setMessage({ text: data.message, type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Error saving order.', type: 'error' });
        } finally {
            setSaving(false);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            <h1 style={{marginTop: '0'}}>Manage Menu Categories</h1>
            <p>Add or remove categories for your menu items. Use the arrows to change their display order on the menu.</p>

            {message.text && (
                <div style={{padding: '15px', borderRadius: '5px', marginBottom: '20px', fontWeight: '500', backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24'}}>
                    {message.text}
                </div>
            )}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '20px'}}>
                {/* Form Side */}
                <div style={{backgroundColor: 'var(--bg-color)', padding: '25px', borderRadius: 'var(--border-radius-md)', boxShadow: 'var(--box-shadow-current)'}}>
                    <h2 style={{marginTop: '0'}}>Add New Category</h2>
                    <form onSubmit={handleAddCategory}>
                        <label style={{display: 'block', marginBottom: '10px', fontWeight: '500'}}>Category Name</label>
                        <input 
                            type="text" 
                            required 
                            value={newCategory} 
                            onChange={(e) => setNewCategory(e.target.value)} 
                            style={{width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '5px', marginBottom: '15px'}} 
                        />
                        <button type="submit" disabled={saving} style={{backgroundColor: 'var(--primary-orange)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer'}}>
                            {saving ? 'Adding...' : 'Add Category'}
                        </button>
                    </form>
                </div>

                {/* List Side */}
                <div style={{backgroundColor: 'var(--bg-color)', padding: '25px', borderRadius: 'var(--border-radius-md)', boxShadow: 'var(--box-shadow-current)'}}>
                    <h2 style={{marginTop: '0'}}>Your Categories</h2>
                    {loading ? (
                        <p>Loading...</p>
                    ) : categories.length === 0 ? (
                        <p>You haven't created any categories yet.</p>
                    ) : (
                        <div>
                            <ul style={{listStyleType: 'none', padding: '0', margin: '0'}}>
                                {categories.map((cat, index) => (
                                    <li 
                                        key={cat.category_id} 
                                        style={{
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            padding: '16px 15px', 
                                            marginBottom: '10px',
                                            backgroundColor: 'var(--card-bg-color)',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                        }}
                                        className="table-row-hover"
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <i className="fas fa-grip-vertical" style={{ color: '#ccc', cursor: 'grab' }} />
                                            <span style={{fontWeight: '600', fontSize: '1.05rem'}}>{cat.category_name}</span>
                                        </div>
                                        <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                                            <div style={{display: 'flex', gap: '8px'}}>
                                                <button type="button" onClick={() => moveCategory(index, -1)} disabled={index === 0} style={{border: '1px solid var(--border-color)', background: 'var(--header-actions-bg)', cursor: index === 0 ? 'not-allowed' : 'pointer', color: index === 0 ? '#ccc' : 'var(--text-color)', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                    <i className="fas fa-chevron-up" style={{fontSize: '0.8em'}} />
                                                </button>
                                                <button type="button" onClick={() => moveCategory(index, 1)} disabled={index === categories.length - 1} style={{border: '1px solid var(--border-color)', background: 'var(--header-actions-bg)', cursor: index === categories.length - 1 ? 'not-allowed' : 'pointer', color: index === categories.length - 1 ? '#ccc' : 'var(--text-color)', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                    <i className="fas fa-chevron-down" style={{fontSize: '0.8em'}} />
                                                </button>
                                            </div>
                                            <div style={{ width: '1px', height: '20px', backgroundColor: 'var(--border-color)' }} />
                                            <button type="button" onClick={() => handleDelete(cat.category_id)} style={{color: 'var(--red-accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', padding: '5px'}} title="Delete">
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <button type="button" onClick={handleSaveOrder} disabled={saving} style={{marginTop: '20px', backgroundColor: 'var(--green-accent)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer'}}>
                                {saving ? 'Saving...' : 'Save Custom Order'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageCategories;
